<template>
<div class="pcp-page">
  <div class="pcp-toolbar no-print">
    <button class="btn btn-sm btn-success" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print / Save as PDF</button>
    <doc-share v-if="pc && !sharedView" class="ms-2" :title="'Payment Calculation — ' + (pc.farm_name || '')" :doc-ref="pc.document_ref" doc-type="payment_calc" :doc-id="pc.id" />
    <button class="btn btn-sm btn-outline-secondary ms-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
  </div>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="pc" class="pcp-sheet">
    <header class="pcp-head">
      <div>
        <div class="pcp-farm">{{ (pc.farm_name||'').toUpperCase() }}</div>
        <div class="pcp-meta">{{ pc.farm_address || '' }}<span v-if="pc.farm_county"> — {{ pc.farm_county }} County</span></div>
        <div v-if="pc.farm_phone" class="pcp-meta">Tel: {{ pc.farm_phone }}</div>
      </div>
      <div class="pcp-title-block">
        <div class="pcp-status" :class="'st-'+pc.status">{{ statusLabel(pc.status) }}</div>
        <div class="pcp-ref">{{ pc.document_ref }}</div>
      </div>
    </header>

    <h2 class="pcp-title">Payment Calculation</h2>
    <div class="pcp-sub">
      <span v-if="pc.slaughter_sheet_ref">Slaughter sheet: <strong>{{ pc.slaughter_sheet_ref }}</strong> · </span>
      <span v-if="pc.buyer_name">Buyer: <strong>{{ pc.buyer_name }}</strong> · </span>
      Date: <strong>{{ fmtDate(pc.calc_date) }}</strong>
    </div>

    <!-- CARCASS PROCEEDS -->
    <h3 class="pcp-section">CARCASS PROCEEDS</h3>
    <table class="pcp-table">
      <tr><td>Total Cold Carcass Weight (kg)</td><td class="amt">{{ pc.total_cold_cw_kg ? Number(pc.total_cold_cw_kg).toLocaleString() + ' kg' : '—' }}</td></tr>
      <tr><td>Agreed Price per kg CW (KES)</td><td class="amt">{{ pc.agreed_price_per_kg_kes ? 'KES ' + Number(pc.agreed_price_per_kg_kes).toLocaleString() : '—' }}</td></tr>
      <tr class="pcp-line"><td>Gross Carcass Value</td><td class="amt"><strong>KES {{ Number(pc.gross_carcass_kes||0).toLocaleString() }}</strong></td></tr>
      <tr><td>Less: Slaughter Fee</td><td class="amt minus">(KES {{ Number(pc.slaughter_fee_kes||0).toLocaleString() }})</td></tr>
      <tr><td>Less: Vet Inspection Fee</td><td class="amt minus">(KES {{ Number(pc.vet_fee_kes||0).toLocaleString() }})</td></tr>
      <tr><td>Less: County Levy</td><td class="amt minus">(KES {{ Number(pc.county_levy_kes||0).toLocaleString() }})</td></tr>
      <tr v-if="pc.discount_kes > 0"><td>Less: Discount</td><td class="amt minus">(KES {{ Number(pc.discount_kes||0).toLocaleString() }})</td></tr>
      <tr v-if="pc.other_deductions_kes > 0"><td>Less: {{ pc.other_deductions_label || 'Other deductions' }}</td><td class="amt minus">(KES {{ Number(pc.other_deductions_kes||0).toLocaleString() }})</td></tr>
      <tr class="pcp-subtotal"><td><strong>NET CARCASS PAYABLE</strong></td><td class="amt"><strong>KES {{ Number(pc.net_carcass_kes||0).toLocaleString() }}</strong></td></tr>
    </table>

    <!-- OFFAL -->
    <h3 class="pcp-section">OFFAL &amp; BY-PRODUCTS</h3>
    <table class="pcp-table">
      <tr><td>Total Hide Value</td><td class="amt">KES {{ Number(pc.hide_value_kes||0).toLocaleString() }}</td></tr>
      <tr><td>Total Liver Value</td><td class="amt">KES {{ Number(pc.liver_value_kes||0).toLocaleString() }}</td></tr>
      <tr><td>Tripe / Rumen Value</td><td class="amt">KES {{ Number(pc.tripe_value_kes||0).toLocaleString() }}</td></tr>
      <tr><td>Heads &amp; Legs Value</td><td class="amt">KES {{ Number(pc.heads_legs_value_kes||0).toLocaleString() }}</td></tr>
      <tr><td>Other Offal Value</td><td class="amt">KES {{ Number(pc.other_offal_value_kes||0).toLocaleString() }}</td></tr>
      <tr class="pcp-subtotal"><td><strong>TOTAL BY-PRODUCTS</strong></td><td class="amt"><strong>KES {{ Number(pc.total_byproducts_kes||0).toLocaleString() }}</strong></td></tr>
    </table>

    <!-- GRAND TOTAL -->
    <div class="pcp-grand">
      <div class="pcp-grand-label">GRAND TOTAL PAYABLE TO SUPPLIER</div>
      <div class="pcp-grand-amount">KES {{ Number(pc.grand_total_kes||0).toLocaleString() }}</div>
    </div>

    <!-- BANK BLOCK -->
    <div v-if="pc.bank_account_name" class="pcp-bank">
      <div class="pcp-bank-title">Payment — RTGS to supplier bank account</div>
      <div class="pcp-bank-grid">
        <div><span class="pcp-l">Account name</span><strong>{{ pc.bank_account_name }}</strong></div>
        <div><span class="pcp-l">Bank</span><strong>{{ pc.bank_name }}</strong></div>
        <div v-if="pc.branch_name"><span class="pcp-l">Branch</span><strong>{{ pc.branch_name }}</strong></div>
        <div><span class="pcp-l">A/C No.</span><strong>{{ pc.account_no }}</strong></div>
        <div v-if="pc.branch_code"><span class="pcp-l">Branch code</span><strong>{{ pc.branch_code }}</strong></div>
        <div v-if="pc.swift_code"><span class="pcp-l">SWIFT</span><strong>{{ pc.swift_code }}</strong></div>
      </div>
    </div>

    <!-- PAID NOTICE -->
    <div v-if="pc.status === 'paid'" class="pcp-paid">
      <i class="mdi mdi-check-circle me-1"></i>PAID
      <span v-if="pc.rtgs_ref"> · Ref: <strong>{{ pc.rtgs_ref }}</strong></span>
      <span v-if="pc.paid_at"> · {{ fmtDate(pc.paid_at) }}</span>
    </div>

    <div v-if="pc.notes" class="pcp-notes"><strong>Notes:</strong> {{ pc.notes }}</div>
  </div>
</div>
</template>

<style>
.pcp-page { background:#F0EDE5; min-height:100vh; padding:30px 16px; color:#1A1A1A; font-family:Arial, sans-serif; }
.pcp-toolbar { max-width:780px; margin:0 auto 16px; display:flex; justify-content:center; }
.pcp-sheet { max-width:780px; margin:0 auto; background:#FFFFFF; border:1px solid #D8D2C2; padding:36px 40px; box-shadow:0 4px 24px rgba(0,0,0,.08); }

.pcp-head { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:14px; border-bottom:2px solid #1A1A1A; margin-bottom:18px; }
.pcp-farm { font-family:Georgia,serif; color:#1A1A1A; font-size:20px; font-weight:700; }
.pcp-meta { font-size:11px; color:#555; margin-top:2px; }
.pcp-title-block { text-align:right; }
.pcp-status { display:inline-block; font-size:11px; font-weight:700; padding:4px 12px; border-radius:3px; letter-spacing:1.2px; text-transform:uppercase; }
.pcp-status.st-draft { background:#E0DDD3; color:#777; }
.pcp-status.st-calculated { background:#D6E9FF; color:#0F4C81; }
.pcp-status.st-approved { background:#C8E6C9; color:#1F6B1F; }
.pcp-status.st-paid { background:#FFE0B2; color:#8B5A2B; }
.pcp-ref { font-family:'Courier New',monospace; color:#555; font-size:11px; margin-top:6px; }

.pcp-title { font-family:Georgia,serif; font-size:22px; letter-spacing:1px; text-align:center; margin:18px 0 4px; color:#1A1A1A; }
.pcp-sub { text-align:center; font-size:11px; color:#666; margin-bottom:20px; }

.pcp-section { font-family:Georgia,serif; font-size:12px; letter-spacing:2px; color:#1A1A1A; background:#F7F5F0; padding:8px 14px; border-left:4px solid #1A1A1A; margin:18px 0 4px; }
.pcp-table { width:100%; border-collapse:collapse; font-size:12px; }
.pcp-table td { padding:8px 12px; border-bottom:1px solid #E5E0D5; }
.pcp-table td.amt { text-align:right; font-family:'Courier New',monospace; font-size:13px; }
.pcp-table td.minus { color:#B71C1C; }
.pcp-line td { background:#FBF8F0; }
.pcp-subtotal td { background:#1A1A1A; color:#FFF; font-family:Georgia,serif; font-size:14px; padding:10px 12px; }

.pcp-grand { background:linear-gradient(135deg,#8B5A2B 0%,#5C4533 100%); color:#F5EEDE; padding:18px 26px; border-radius:3px; margin:22px 0; display:flex; justify-content:space-between; align-items:center; }
.pcp-grand-label { font-family:Georgia,serif; font-size:13px; letter-spacing:2px; }
.pcp-grand-amount { font-family:'Courier New',monospace; font-size:22px; font-weight:700; letter-spacing:1.5px; }

.pcp-bank { background:#FBF8F0; border:1px solid #D8D2C2; padding:14px 18px; margin-bottom:14px; }
.pcp-bank-title { font-family:Georgia,serif; font-size:12px; letter-spacing:1px; color:#1A1A1A; margin-bottom:10px; padding-bottom:6px; border-bottom:1px dotted #D8D2C2; }
.pcp-bank-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px 18px; font-size:11px; }
.pcp-bank-grid > div { display:flex; flex-direction:column; }
.pcp-l { font-size:9px; color:#888; text-transform:uppercase; letter-spacing:1px; }
.pcp-bank-grid strong { font-size:12px; color:#1A1A1A; font-family:'Courier New',monospace; }

.pcp-paid { background:#C8E6C9; color:#1F6B1F; padding:12px 14px; text-align:center; font-size:14px; font-weight:700; letter-spacing:1.5px; border-radius:3px; margin:14px 0; }
.pcp-notes { font-size:11px; color:#555; padding:10px 14px; background:#F7F5F0; border-left:3px solid #1A1A1A; margin-top:14px; }

@media print {
  .no-print { display:none !important; }
  .pcp-page { background:#fff; padding:0; }
  .pcp-sheet { box-shadow:none; border:none; max-width:none; }
}

/* On-screen mobile preview — even though this is a printable, users do preview on phones */
@media (max-width: 768px) {
  .pcp-page { padding: 12px; }
  .pcp-sheet { padding: 16px; border-radius: 6px; }
  .pcp-sheet h1, .pcp-sheet h2 { font-size: 16px; }
  .pcp-table { font-size: 11px; }
  .pcp-table td, .pcp-table th { padding: 4px 6px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
import DocShare from "@/components/flockr-farm/DocShare.vue";
export default {
  name: "PaymentCalculationPrint",
  components: { DocShare },
  data(){ return { loading:true, pc:null }; },
  computed: { sharedView(){ return !!this.$route.query.t; } },
  mounted(){
    var token = this.$route.query.t;
    if (token) this.loadByToken(token);
    else this.load(this.$route.params.id);
  },
  methods:{
    fmtDate(d){ if(!d)return ''; try { return new Date(d).toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}); } catch(e){ return d; } },
    statusLabel(s){ return ({draft:'Draft',calculated:'Calculated',approved:'Approved',paid:'Paid'})[s] || s; },
    async load(id){
      this.loading = true;
      try { var r = await executeJson({ function:'getPaymentCalculation', paymentCalcId:id }, flocker); if (r.data && r.data.success) this.pc = r.data.data.paymentCalculation; }
      catch(e){ console.error(e); }
      this.loading = false;
    },
    async loadByToken(token){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getSharedDocument', token: token }, flocker);
        if (r.data && r.data.success) this.pc = r.data.data.paymentCalculation;
        else alert((r.data && r.data.message) || 'This share link is no longer valid.');
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    doPrint(){ window.print(); }
  }
};
</script>
